import { useState, useEffect } from "react"
import { Alert, Button, Col, Form, Input, message, Row, Space, Upload, Select } from "antd"
import { MinusCircleOutlined, PlusOutlined, UploadOutlined, LeftOutlined } from "@ant-design/icons"
import "../assets/styles/AddProduct.css"
import { createProduct, getCategories, updateProduct } from "../services/ProductServices"
import { 
  getProductImageUploadPolicy, 
  getProductDocumentUploadPolicy, 
  uploadFileWithSignedUrl,
  getFileExtension,
  getContentTypeFromExtension
} from "../services/FileUploadServices"
import { useNavigate } from "react-router-dom"
import { useAuth } from "../contexts/authContext"

const { TextArea } = Input

const AddProduct = () => {
  const [form] = Form.useForm()
  const [materiales, setMateriales] = useState([])
  const [imagenes, setImagenes] = useState([])
  const [fichaTecnica, setFichaTecnica] = useState(null)
  const [medidas, setMedidas] = useState([{ medida: "" }]) // Inicializar con una medida vacía
  const [error, setError] = useState("")
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [certificaciones, setCertificaciones] = useState([])
  const [categoria, setCategoria] = useState("")
  const [availableCategories, setAvailableCategories] = useState([])
  const [loadingCategories, setLoadingCategories] = useState(true)

  const { getToken } = useAuth()

  // Cargar categorías existentes al montar el componente
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true)
        const categories = await getCategories()
        setAvailableCategories(categories)
      } catch (error) {
        console.error("Error loading categories:", error)
        message.error("Error al cargar las categorías")
      } finally {
        setLoadingCategories(false)
      }
    }

    loadCategories()
  }, [])

  // COMENTADO: Sistema antiguo de cantidades fijas para precios
  // const cantidadesFijas = [
  //   { cantidad: 1, label: "1ud" },
  //   { cantidad: 10, label: "10uds" },
  //   { cantidad: 50, label: "50uds" },
  //   { cantidad: 100, label: "100uds" },
  //   { cantidad: 200, label: "200uds" },
  //   { cantidad: 300, label: "300uds" },
  //   { cantidad: 500, label: "500uds" },
  //   { cantidad: 1000, label: "1000uds" },
  // ]

  const handleSubmit = async (values) => {
    setLoading(true)
    setError("")

    const { nombre, detalle, uso, material, certificaciones: certificacionesString, categoria } = values
    const certificaciones = certificacionesString.split(",").map((cert) => cert.trim())

    if (!nombre || !detalle || !uso || !material || !certificaciones || !categoria) {
      setError("Todos los campos son obligatorios, excepto la ficha técnica.")
      setLoading(false)
      return
    }

    if (imagenes.length < 1) {
      setError("Debe subir al menos una imagen del producto.")
      setLoading(false)
      return
    }

    if (medidas.length === 0) {
      setError("Debe agregar al menos una medida.")
      setLoading(false)
      return
    }

    // NUEVO: Validación simplificada - solo verificar que tenga nombre de medida
    const medidasInvalidas = medidas.some((medida, index) => {
      if (!medida.medida || medida.medida.trim() === "") {
        setError(`La medida ${index + 1} debe tener un nombre.`)
        return true
      }
      return false
    })

    if (medidasInvalidas) {
      setLoading(false)
      return
    }

    // COMENTADO: Validación antigua con stock y precios
    // const medidasInvalidas = medidas.some((medida, index) => {
    //   if (!medida.medida || medida.stock === undefined || medida.stock === null || !medida.precios[0].valorUnitario) {
    //     setError(
    //       `La medida ${index + 1} no está completa. Asegúrese de ingresar la medida, el stock y el precio por 1 unidad.`,
    //     )
    //     return true
    //   }
    //   return false
    // })

    // if (medidasInvalidas) {
    //   setLoading(false)
    //   return
    // }

    if (imagenes.length > 10) {
      message.error("Solo puedes subir un máximo de 10 imágenes.")
      setLoading(false)
      return
    }

    const token = await getToken()

    try {
      // NUEVO: Transformar medidas a formato simple (solo nombre, sin stock ni precios)
      const medidasSimples = medidas.map(medida => ({
        medida: medida.medida.trim()
      }))

      // 1. Crear el producto primero para obtener el ID
      const createResponse = await createProduct(
        token,
        nombre,
        detalle,
        uso,
        materiales,
        certificaciones,
        [], // Imágenes vacías por ahora
        medidasSimples, // Usar medidas simplificadas
        null, // Ficha técnica vacía por ahora
        categoria,
      )

      // 2. Extraer el ID del producto de la respuesta
      const productId = createResponse.product_id || createResponse.id

      // 3. Subir imágenes al bucket
      const uploadedImageUrls = []
      for (let i = 0; i < imagenes.length; i++) {
        const img = imagenes[i]
        if (img.originFileObj && (img.originFileObj instanceof Blob || img.originFileObj instanceof File)) {
          try {
            const fileExtension = getFileExtension(img.name)
            const contentType = getContentTypeFromExtension(fileExtension)

            // Obtener política de subida
            const uploadPolicy = await getProductImageUploadPolicy(
              token,
              productId,
              fileExtension,
              contentType
            )

            // Subir archivo usando la URL firmada
            await uploadFileWithSignedUrl(
              uploadPolicy.signed_url,
              img.originFileObj,
              contentType
            )

            // Agregar la ruta del archivo al bucket
            uploadedImageUrls.push(uploadPolicy.file_path)

          } catch (error) {
            console.error("Error al subir la imagen:", error)
            message.error(`Error al subir la imagen ${i + 1}: ${error.message}`)
          }
        }
      }

      // 4. Subir ficha técnica si existe
      let technicalSheetUrl = null
      if (fichaTecnica && fichaTecnica.originFileObj) {
        try {
          const fileExtension = getFileExtension(fichaTecnica.name)
          const contentType = getContentTypeFromExtension(fileExtension)

          const uploadPolicy = await getProductDocumentUploadPolicy(
            token,
            productId,
            fichaTecnica.name,
            contentType
          )

          await uploadFileWithSignedUrl(
            uploadPolicy.signed_url,
            fichaTecnica.originFileObj,
            contentType
          )

          technicalSheetUrl = uploadPolicy.file_path

        } catch (error) {
          console.error("Error al subir la ficha técnica:", error)
          message.error(`Error al subir la ficha técnica: ${error.message}`)
        }
      }

      // 5. Actualizar el producto con las URLs de las imágenes y ficha técnica
      if (uploadedImageUrls.length > 0 || technicalSheetUrl) {
        try {
          await updateProduct(
            productId,
            token,
            nombre,
            detalle,
            uso,
            materiales,
            certificaciones,
            uploadedImageUrls,
            medidasSimples, // Usar medidas simplificadas
            technicalSheetUrl,
            categoria
          )
        } catch (error) {
          console.error("Error al actualizar el producto con las imágenes:", error)
          message.error("Producto creado pero hubo un problema al actualizar las imágenes")
        }
      }

      message.success("Producto agregado correctamente")
      console.log(createResponse)
      form.resetFields()
      setImagenes([])
      setFichaTecnica(null)
      navigate("/myproducts")
    } catch (error) {
      console.error(error)
      message.error("Hubo un problema al agregar el producto")
    } finally {
      setLoading(false)
    }
  }

  const handleFileChange = (info, type) => {
    if (type === "imagenes") {
      setImagenes(info.fileList)
      if (info.fileList.length === 0) {
        setError("")
      }
    } else if (type === "fichaTecnica") {
      setFichaTecnica(info.fileList[0]) // Cambiado de info.file a info.fileList[0]
    }
    if (info && info.file && info.file.status === "done") {
      info.file.status = "success"
    }
  }

  const handleMaterialesChange = (e) => {
    setMateriales(e.target.value.split(",").map((item) => item.trim()))
  }

  const handleCertificacionesChange = (e) => {
    const certifications = e.target.value.split(",").map((item) => item.trim())
    setCertificaciones(certifications)
    form.setFieldsValue({ certificaciones: certifications.join(", ") })
  }

  const handleCategoriaChange = (value) => {
    // Con mode="tags", value es un array, tomamos el primer elemento
    const selectedCategory = Array.isArray(value) ? value[0] : value
    setCategoria(selectedCategory)
    form.setFieldsValue({ categoria: selectedCategory })
    
    // Si es una nueva categoría que no existe en la lista, la agregamos
    if (selectedCategory && !availableCategories.includes(selectedCategory)) {
      setAvailableCategories(prev => [...prev, selectedCategory].sort())
      message.success(`Nueva categoría "${selectedCategory}" agregada`)
    }
  }

  // NUEVO: Manejo simplificado de medidas (solo nombre)
  const handleMedidasChange = (index, value) => {
    const newMedidas = [...medidas]
    newMedidas[index] = {
      medida: value
    }
    setMedidas(newMedidas)
  }

  const addMedida = () => {
    setMedidas([
      ...medidas,
      {
        medida: ""
      },
    ])
  }

  // COMENTADO: Sistema antiguo con stock y precios
  // const handleMedidasChange = (index, field, value, priceIndex) => {
  //   const newMedidas = [...medidas]
  //   if (field === "precios") {
  //     const newPrecios = [...newMedidas[index].precios]
  //     if (priceIndex !== undefined) {
  //       const precio = newPrecios[priceIndex]
  //       if (value === undefined) {
  //         precio.valorUnitario = undefined
  //         precio.precioTotal = undefined
  //       } else if (typeof value === "object") {
  //         if ("valorUnitario" in value && value.valorUnitario !== undefined) {
  //           precio.valorUnitario = value.valorUnitario
  //           precio.precioTotal = value.valorUnitario * precio.cantidad
  //         } else if ("precioTotal" in value && value.precioTotal !== undefined) {
  //           precio.precioTotal = value.precioTotal
  //           precio.valorUnitario = value.precioTotal / precio.cantidad
  //         }
  //       }
  //     }
  //     newMedidas[index].precios = newPrecios
  //   } else {
  //     newMedidas[index][field] = value
  //   }
  //   setMedidas(newMedidas)
  // }

  // const addMedida = () => {
  //   setMedidas([
  //     ...medidas,
  //     {
  //       medida: "",
  //       stock: undefined,
  //       precios: cantidadesFijas.map((item) => ({ ...item, valorUnitario: undefined, precioTotal: undefined })),
  //     },
  //   ])
  // }

  const removeMedida = (index) => {
    const newMedidas = [...medidas]
    newMedidas.splice(index, 1)
    setMedidas(newMedidas)
  }

  const beforeUpload = (file) => {
    const isImage = file.type.startsWith("image/")
    const isPDF = file.type === "application/pdf"
    if (!isImage && !isPDF) {
      message.error("Solo puedes subir archivos de imagen o PDF!")
    } else {
      setError("")
    }
    return false
  }

  const handleGoBack = () => {
    navigate("/myproducts")
  }

  return (
    <div className="add-product-page">
      <h2>Agregar Nuevo Producto</h2>
      <Form form={form} onFinish={handleSubmit} layout="vertical">
        <Row gutter={16} className="form-row">
          <Col xs={24} md={8} className="form-col">
            <Form.Item label="Nombre del Producto" name="nombre" rules={[{ required: true, message: "" }]}>
              <Input className="tall-input" />
            </Form.Item>
            <Form.Item label="Materiales" name="material" rules={[{ required: true, message: "" }]}>
              <Input className="tall-input" placeholder="Separados por coma" onChange={handleMaterialesChange} />
            </Form.Item>
            <Form.Item label="Certificaciones" name="certificaciones" rules={[{ required: true, message: "" }]}>
              <Input
                className="tall-input"
                placeholder="Separadas por coma"
                onChange={handleCertificacionesChange}
                value={certificaciones.join(", ")}
              />
            </Form.Item>
            <Form.Item
              label="Categoría"
              name="categoria"
              rules={[{ required: true, message: "" }]}
            >
              <Select
                className="tall-input"
                placeholder="Seleccione una categoría o escriba una nueva"
                onChange={handleCategoriaChange}
                value={categoria ? [categoria] : []}
                showSearch
                allowClear
                mode="tags"
                maxTagCount={1}
                filterOption={(input, option) =>
                  (option?.children ?? '').toLowerCase().includes(input.toLowerCase())
                }
                onSearch={(value) => {
                  // Si el usuario escribe algo que no existe, se puede agregar como nueva categoría
                  if (value && !availableCategories.includes(value)) {
                    // La nueva categoría se agregará automáticamente cuando se seleccione
                  }
                }}
              >
                {loadingCategories ? (
                  <Select.Option value="">Cargando categorías...</Select.Option>
                ) : availableCategories.length === 0 ? (
                  <Select.Option value="">No hay categorías disponibles</Select.Option>
                ) : (
                  availableCategories.map((cat) => (
                    <Select.Option key={cat} value={cat}>
                      {cat}
                    </Select.Option>
                  ))
                )}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={16} className="form-col right-column">
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item label="Detalle" name="detalle" rules={[{ required: true, message: "" }]}>
                  <TextArea rows={4} />
                </Form.Item>
                <Form.Item label="Uso" name="uso" rules={[{ required: true, message: "" }]}>
                  <TextArea rows={4} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Row justify={"space-between"}>
                  <Col xs={15} md={24}>
                    <Form.Item label="Imágenes" name="imagenes" className="custom-image-upload">
                      <Upload
                        listType="picture-card"
                        fileList={imagenes}
                        onChange={(info) => handleFileChange(info, "imagenes")}
                        beforeUpload={beforeUpload}
                        multiple={true}
                        accept="image/*"
                      >
                        <div>
                          <UploadOutlined />
                          <div style={{ marginTop: 8 }}>Subir Imágenes</div>
                        </div>
                      </Upload>
                    </Form.Item>
                  </Col>
                  <Col xs={24} sm={8} md={24}>
                    <Form.Item label="Ficha Técnica" name="ficha_tecnica">
                      <Upload
                        onChange={(info) => handleFileChange(info, "fichaTecnica")}
                        beforeUpload={() => false}
                        accept="application/pdf,image/*"
                        fileList={fichaTecnica ? [fichaTecnica] : []}
                      >
                        <Button icon={<UploadOutlined />}>
                          {fichaTecnica ? "Cambiar Ficha Técnica" : "Subir Ficha Técnica"}
                        </Button>
                      </Upload>
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Col>
        </Row>
        <Row>
          <Col xs={24} md={16} className="medidas-col">
            <Form.Item label="Medidas" className="medidas-precios">
              {/* NUEVO: Sistema simplificado de medidas (solo nombre) */}
              <Space direction="vertical" style={{ width: "100%" }}>
                {medidas.map((medida, index) => (
                  <Row key={index} gutter={16} align="middle" style={{ marginBottom: 16 }}>
                    <Col xs={18} sm={20}>
                      <Input
                        placeholder={`Nombre de la medida ${index + 1}`}
                        value={medida.medida}
                        onChange={(e) => handleMedidasChange(index, e.target.value)}
                        size="large"
                      />
                    </Col>
                    <Col xs={6} sm={4}>
                      <Button
                        type="text"
                        danger
                        icon={<MinusCircleOutlined />}
                        onClick={() => removeMedida(index)}
                        disabled={medidas.length === 1}
                        size="large"
                      >
                        Eliminar
                      </Button>
                    </Col>
                  </Row>
                ))}
              </Space>
              <Form.Item>
                <Button type="dashed" onClick={addMedida} block icon={<PlusOutlined />} className="add-medida-button">
                  Agregar Medida
                </Button>
              </Form.Item>
            </Form.Item>

            {/* COMENTADO: Sistema antiguo con stock y precios */}
            {/* <Form.Item label="Medidas y Precios" className="medidas-precios">
              <Collapse className="medidas-collapse">
                {medidas.map((medida, index) => (
                  <Panel
                    header={`Medida ${index + 1}: ${medida.medida || "Nueva medida"}`}
                    key={index}
                    extra={
                      <MinusCircleOutlined
                        onClick={(event) => {
                          event.stopPropagation()
                          removeMedida(index)
                        }}
                      />
                    }
                  >
                    <Space direction="vertical" style={{ width: "100%", paddingTop: "16px" }}>
                      <Row gutter={16}>
                        <Col xs={24} md={12}>
                          <div>
                            <strong>Medida</strong>
                            <Input
                              placeholder="Medida"
                              value={medida.medida}
                              onChange={(e) => handleMedidasChange(index, "medida", e.target.value)}
                              style={{ width: "100%" }}
                            />
                          </div>
                        </Col>
                        <Col xs={24} md={12}>
                          <div>
                            <strong>Stock</strong>
                            <InputNumber
                              placeholder="Stock"
                              value={medida.stock}
                              onChange={(value) => handleMedidasChange(index, "stock", value)}
                              style={{ width: "100%" }}
                            />
                          </div>
                        </Col>
                      </Row>
                      <Row gutter={[16, 16]} align="middle" style={{ marginTop: "16px" }}>
                        <Col xs={24} md={6}>
                          <strong>Cantidad</strong>
                        </Col>
                        <Col xs={24} md={9}>
                          <strong>Precio unitario</strong>
                        </Col>
                        <Col xs={24} md={9}>
                          <strong>Precio total</strong>
                        </Col>
                      </Row>
                      {medida.precios.map((precio, i) => (
                        <Row gutter={[16, 16]} key={i} align="middle">
                          <Col xs={24} md={6}>
                            <span>{precio.label}</span>
                          </Col>
                          <Col xs={24} md={9}>
                            <InputNumber
                              placeholder="Precio unitario"
                              value={precio.valorUnitario}
                              onChange={(value) => handleMedidasChange(index, "precios", { valorUnitario: value }, i)}
                              style={{ width: "100%" }}
                            />
                          </Col>
                          <Col xs={24} md={9}>
                            <InputNumber
                              placeholder="Precio total"
                              value={precio.precioTotal}
                              onChange={(value) => handleMedidasChange(index, "precios", { precioTotal: value }, i)}
                              style={{ width: "100%" }}
                            />
                          </Col>
                        </Row>
                      ))}
                    </Space>
                  </Panel>
                ))}
              </Collapse>
              <Form.Item>
                <Button type="dashed" onClick={addMedida} block icon={<PlusOutlined />} className="add-medida-button">
                  Agregar Medida
                </Button>
              </Form.Item>
            </Form.Item> */}
          </Col>
          <Col xs={24} md={8} className="submit-button-col">
            <Form.Item className="submit-button-container">
              {error && (
                <Alert message="Error" description={error} type="error" showIcon style={{ marginBottom: "16px" }} />
              )}
              <Button type="primary" htmlType="submit" className="submit-button" loading={loading}>
                Guardar Producto
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
      <Button type="primary" icon={<LeftOutlined />} onClick={handleGoBack} className="back-button">
        Volver
      </Button>
    </div>
  )
}

export default AddProduct

